'use client';
import React from 'react';
import { Distributor, getEBRate, DISTRIBUTORS } from '@/data/mockData';

interface Props {
  distributor: Distributor;
  detailed?: boolean;
}

export default function BonusBreakdownPanel({ distributor, detailed }: Props) {
  const isExec = ['Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(distributor.rank);
  const ebRate = getEBRate(distributor.gsv);
  const hasEEB = distributor.gsv >= 3000;
  const hasDblG1 = distributor.gsv >= 3000;

  const retailProfit = distributor.psv * 0.25;
  const l1Bonus = distributor.children.reduce((sum, id) => {
    const child = DISTRIBUTORS[id];
    return sum + (child ? child.psv * 0.05 : 0);
  }, 0);
  const execBonus = isExec ? distributor.gsv * ebRate : 0;
  const eebBonus = isExec && hasEEB ? distributor.gsv * 0.05 : 0;
  const dblG1Bonus = isExec && hasDblG1 && distributor.g1BreakawayCount > 0 ? distributor.gsv * 0.10 : 0;
  const breakawayBonus = isExec && distributor.g1BreakawayCount >= 1 ? distributor.gsv * 0.025 : 0;
  const totalComm = retailProfit + l1Bonus + execBonus + eebBonus + dblG1Bonus || 1;

  const volumePath = execBonus + eebBonus + dblG1Bonus;
  const depthPath = execBonus + breakawayBonus;
  const wealthPath = volumePath >= depthPath ? 'Volume Maximizer' : 'Depth Maximizer';

  const components = [
    { label: 'Gross Retail Profit', desc: `${distributor.psv} PSV × 25% markup`, value: retailProfit, color: '#34d399', pct: (retailProfit / totalComm) * 100 },
    { label: 'L1 Bonus (5% on sponsored PSV)', desc: `${distributor.children.length} personally sponsored`, value: l1Bonus, color: '#60a5fa', pct: (l1Bonus / totalComm) * 100 },
    { label: `Executive Bonus (${(ebRate * 100).toFixed(0)}% EB)`, desc: isExec ? `GSV tier: ${(distributor.gsv / 1000).toFixed(1)}K` : 'Requires Executive rank', value: execBonus, color: '#a78bfa', pct: (execBonus / totalComm) * 100 },
    { label: 'Extra Executive Bonus (EEB +5%)', desc: hasEEB ? 'GSV ≥ 3,000 — extra 5% on GCSV' : 'Requires 3,000+ GSV', value: eebBonus, color: '#c084fc', pct: (eebBonus / totalComm) * 100 },
    { label: 'Double G1 Bonus (DBLG1 10%)', desc: hasDblG1 && distributor.g1BreakawayCount > 0 ? `${distributor.g1BreakawayCount} G1 execs × 10% GSV` : 'Requires 3,000+ GSV + G1 execs', value: dblG1Bonus, color: '#fbbf24', pct: (dblG1Bonus / totalComm) * 100 },
    { label: 'Breakaway Executive Bonus', desc: distributor.g1BreakawayCount >= 1 ? `${distributor.g1BreakawayCount} G1 execs (2.5–5%)` : 'No G1 breakaway executives', value: breakawayBonus, color: '#f87171', pct: (breakawayBonus / totalComm) * 100 },
  ];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-300">Bonus Component Breakdown</h3>
        <span className="text-xs text-zinc-600">Apr 2026</span>
      </div>

      {/* Total */}
      <div className="bg-gradient-to-r from-violet-900/30 to-violet-800/10 border border-violet-500/20 rounded-xl p-3 mb-4">
        <p className="text-[10px] uppercase tracking-widest text-violet-400 font-semibold">Total Estimated Commission</p>
        <p className="text-3xl font-bold text-violet-200 font-mono tabular-nums mt-0.5">${totalComm.toFixed(2)}</p>
        {isExec && (
          <p className="text-[10px] text-violet-400/60 mt-0.5">
            Wealth Maximizer: <span className="text-amber-400 font-semibold">{wealthPath}</span> selected
            (${Math.max(volumePath, depthPath).toFixed(2)} vs ${Math.min(volumePath, depthPath).toFixed(2)})
          </p>
        )}
      </div>

      {/* Component Bars */}
      <div className="space-y-3">
        {components.map(comp => (
          <div key={`bcomp-${comp.label}`} className={`${comp.value === 0 ? 'opacity-40' : ''}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex-1 min-w-0 pr-3">
                <p className="text-[11px] font-semibold text-zinc-300 truncate">{comp.label}</p>
                <p className="text-[9px] text-zinc-600">{comp.desc}</p>
              </div>
              <span className="text-sm font-bold font-mono flex-shrink-0" style={{ color: comp.color }}>
                ${comp.value.toFixed(2)}
              </span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(comp.pct, 100)}%`, backgroundColor: comp.color }}
              />
            </div>
          </div>
        ))}
      </div>

      {detailed && isExec && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="bg-zinc-800/40 border border-zinc-700 rounded-xl p-3">
            <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold mb-1">Volume Maximizer</p>
            <p className="text-xs text-zinc-500 mb-1">EB + EEB + DBLG1</p>
            <p className={`text-lg font-bold font-mono ${volumePath >= depthPath ? 'text-amber-400' : 'text-zinc-400'}`}>
              ${volumePath.toFixed(2)}
            </p>
            {volumePath >= depthPath && <p className="text-[9px] text-amber-400 font-semibold mt-0.5">✓ Active Path</p>}
          </div>
          <div className="bg-zinc-800/40 border border-zinc-700 rounded-xl p-3">
            <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold mb-1">Depth Maximizer</p>
            <p className="text-xs text-zinc-500 mb-1">EB + Breakaway EB</p>
            <p className={`text-lg font-bold font-mono ${depthPath > volumePath ? 'text-amber-400' : 'text-zinc-400'}`}>
              ${depthPath.toFixed(2)}
            </p>
            {depthPath > volumePath && <p className="text-[9px] text-amber-400 font-semibold mt-0.5">✓ Active Path</p>}
          </div>
        </div>
      )}
    </div>
  );
}